import Stripe from 'stripe';

import { UsersAPIName } from 'src/constants';
import { API } from 'src/utils/AmplifyApiUtils';

import { ApiTags, appAPI } from '.';

/**
 * UnverifiedBankAccount represents a bank account awaiting microdeposit verification.
 * Short-lived record - deleted once verification completes and payment method is attached.
 */
export type UnverifiedBankAccount = {
  id: string;
  portalId: string;
  companyId: string;
  customerId: string;
  setupIntentId: string;
  paymentMethodId: string;
  bankName: string;
  last4: string;
  accountHolderName: string;
  accountHolderType: string;
  createdAt: string;
};

export const pendingBankAccountsApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    createBankSetupIntent: build.mutation<
      { clientSecret: string; setupIntentId: string },
      void
    >({
      queryFn: async () => {
        const result = await API.post(
          UsersAPIName,
          `/paymentMethods/bank-setup-intent`,
          {},
        );
        return { data: result };
      },
    }),
    attachBankPaymentMethod: build.mutation<
      Stripe.PaymentMethod,
      { companyId: string; paymentMethodId: string }
    >({
      queryFn: async (params) => {
        const result = await API.post(
          UsersAPIName,
          `/paymentMethods/attach-bank`,
          {
            body: {
              companyId: params.companyId,
              paymentMethodId: params.paymentMethodId,
            },
          },
        );
        return { data: result };
      },
      invalidatesTags: [ApiTags.stripeCustomer],
    }),
    createUnverifiedBankAccount: build.mutation<
      UnverifiedBankAccount,
      { companyId: string; setupIntentId: string; paymentMethodId: string }
    >({
      queryFn: async (params) => {
        const result = await API.post(UsersAPIName, `/pendingBankAccounts`, {
          body: {
            companyId: params.companyId,
            setupIntentId: params.setupIntentId,
            paymentMethodId: params.paymentMethodId,
          },
        });
        return { data: result };
      },
      invalidatesTags: [ApiTags.bankAccounts],
    }),
    verifyUnverifiedBankAccount: build.mutation<
      void,
      { pendingBankAccountId: string; amounts: number[] }
    >({
      queryFn: async (params) => {
        try {
          const result = await API.post(
            UsersAPIName,
            `/pendingBankAccounts/verify`,
            {
              body: {
                pendingBankAccountId: params.pendingBankAccountId,
                amounts: params.amounts,
              },
            },
          );
          return { data: result };
        } catch {
          return {
            error: {
              status: 400,
              data: {
                message:
                  'Failed to verify bank account. Please ensure your microdeposit amounts are correct.',
              },
            },
          };
        }
      },
      invalidatesTags: [
        ApiTags.bankAccounts,
        ApiTags.paymentMethods,
        ApiTags.stripeCustomer,
      ],
    }),
    deleteUnverifiedBankAccount: build.mutation<void, string>({
      queryFn: async (unverifiedBankAccountId) => {
        const result = await API.del(
          UsersAPIName,
          `/pendingBankAccounts/${unverifiedBankAccountId}`,
          {},
        );
        return { data: result };
      },
      invalidatesTags: [ApiTags.bankAccounts],
    }),
    getUnverifiedBankAccounts: build.query<UnverifiedBankAccount[], string>({
      queryFn: async (companyId) => {
        const result = await API.get(UsersAPIName, `/pendingBankAccounts`, {
          queryStringParameters: {
            companyId,
          },
        });
        return { data: result || [] };
      },
      providesTags: [ApiTags.bankAccounts],
    }),
  }),
});

export const {
  useCreateBankSetupIntentMutation,
  useAttachBankPaymentMethodMutation,
  useCreateUnverifiedBankAccountMutation,
  useVerifyUnverifiedBankAccountMutation,
  useDeleteUnverifiedBankAccountMutation,
  useGetUnverifiedBankAccountsQuery,
} = pendingBankAccountsApi;
